import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Send, X, Mic, Square, Maximize2, Minimize2, Volume2, VolumeX, Camera, Loader2 } from "lucide-react";
import { useApp, API } from "../context";
import { usePremiumAccess } from "../hooks/usePremiumAccess";
import { onActiveProblem, onWrongAnswer, onLearnContext } from "../lib/activeProblem";
import { playLawrence, stopLawrence, onNarration } from "../lib/lawrenceVoice";
import { useFabsCollapsed } from "../lib/fabTidy";
import { useDraggableFab } from "../lib/useDraggableFab";
import { RichText } from "../lib/richText";

const NPC_MAP = {
  // Learning surfaces keep Professor Owl (the learning guide). Everywhere else
  // is Isaiah — one friendly guide for the whole app.
  "/learn": { id: "professor-owl", emoji: "🦉", name: "Professor Owl", color: "#22C55E" },
  "/learning-path": { id: "professor-owl", emoji: "🦉", name: "Professor Owl", color: "#22C55E" },
  "/academy": { id: "professor-owl", emoji: "🦉", name: "Professor Owl", color: "#22C55E" },
  default: { id: "isaiah", emoji: "🌟", name: "Isaiah", color: "#3B82F6" },
};

function getNPC(pathname) {
  for (const [prefix, npc] of Object.entries(NPC_MAP)) {
    if (prefix !== "default" && pathname.startsWith(prefix)) return npc;
  }
  return NPC_MAP.default;
}

const NPCGuide = () => {
  const { user, sounds } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const fabsCollapsed = useFabsCollapsed();
  const owlDrag = useDraggableFab("iw_fab_pos_owl", 256);
  const [open, setOpen] = useState(false);
  // Tap-to-expand: blow the chat up to a near-fullscreen sheet so long
  // conversations are easy to read on a phone. Resets when the chat closes.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (!open) setExpanded(false); }, [open]);
  // Site-wide: tap the little X to slide the helper off-screen, leaving a small
  // peek tab in the corner to bring it back (same idea as the Back button).
  const [npcHidden, setNpcHidden] = useState(() => {
    try { return localStorage.getItem("npc_hidden") === "1"; } catch { return false; }
  });
  const hideNpc = () => { try { localStorage.setItem("npc_hidden", "1"); } catch {} ; setNpcHidden(true); setOpen(false); };
  const showNpc = () => { try { localStorage.removeItem("npc_hidden"); } catch {} ; setNpcHidden(false); };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);   // read-aloud (item 2)
  const [readSent, setReadSent] = useState(-1);            // active sentence while reading aloud
  const [readWord, setReadWord] = useState(-1);            // active word within that sentence (word glow)
  const readGenRef = useRef(0);

  // Owl Word Glow: follow the voice engine's playback fraction and light up the
  // exact word being spoken, so the earliest readers can track along.
  useEffect(() => onNarration((ev) => {
    if (ev.type === "progress" && typeof ev.fraction === "number") {
      const words = String(ev.text || "").trim().split(/\s+/).filter(Boolean);
      if (words.length) setReadWord(Math.min(words.length - 1, Math.floor(ev.fraction * words.length)));
    } else if (ev.type === "start") {
      setReadWord(0);
    } else if (ev.type === "end") {
      setReadWord(-1);
    }
  }), []);
  const [autoRead, setAutoRead] = useState(() => { try { return localStorage.getItem("academy_owl_autoread") === "1"; } catch { return false; } });
  const lastReadRef = useRef(-1);
  const [uploading, setUploading] = useState(false);       // snap-a-question (item 4)
  const [selection, setSelection] = useState(null);        // explain-highlight (item 3)
  const fileInputRef = useRef(null);
  const wrongCountRef = useRef(0);                          // auto-offer throttle (item 1)
  const chatEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const baseNpc = getNPC(location.pathname);
  // Inside the Learning Academy (either /learn/academy/* or the lesson player
  // at /academy/lesson/*), the floating guide is ALWAYS Professor Owl and is
  // ALWAYS available, so a learner stuck on a subject, lesson, quiz, or test
  // can ask without leaving the screen. (June 2026)
  const inAcademyRoute = location.pathname.startsWith("/learn/academy")
    || location.pathname.startsWith("/academy/")
    || location.pathname === "/academy";
  // Inside LITERACY CORNER (/learn/literacy/*) Professor Owl is ALSO always
  // available — the "extra tutor you hired on the side" for homework help,
  // riding alongside Mrs. Julia (the in-class reading instructor). Same
  // always-on treatment as the Academy. (June 2026)
  const inLiteracyRoute = location.pathname.startsWith("/learn/literacy");
  // Routes where Owl is the always-on private tutor (never hidden, never
  // premium-gated per-page) — the Learning Academy and Literacy Corner.
  const alwaysOwlRoute = inAcademyRoute || inLiteracyRoute;
  const npc = alwaysOwlRoute ? NPC_MAP["/learn"] : baseNpc;
  // Hide the floating NPC on individual game pages so it doesn't sit on top
  // of the Rematch button / D-pad area on mobile. The user feedback was
  // explicit: "if someone ever wanted to go home, they can't do it" -
  // the bot was occluding the bottom-nav home button. So we hide it on
  // any concrete game route (`/arcade/<slug>`, `/learn/<activity>/<id>`)
  // and only show it on the world hubs (`/arcade`, `/learn`) where
  // there's plenty of breathing room. (iter408)
  const path = location.pathname;
  const isInsideGame = (
    (path.startsWith("/arcade/") && path.length > "/arcade/".length) ||
    (path.startsWith("/learn/") && path.split("/").filter(Boolean).length >= 3)
  );
  // Any Explore-the-Forest subject/practice page (but NOT the /learn hub or the
  // full Tutor thread). Professor Owl is a PREMIUM perk here, so non-premium
  // learners must never see it pop up while practicing.
  const onLearnSubject = path.startsWith("/learn/") && !path.startsWith("/learn/tutor");
  // When the floating NPC is Professor Owl (i.e., on the Learn pages),
  // we treat the conversation as a continuation of the user's main
  // Tutor message thread (the one rendered on /learn/tutor). Both
  // sides write to the same `/api/tutor/messages` collection so the
  // user can ask a quick question from the floating widget AND see
  // it later in the full thread, or vice versa. (May 2026)
  const isOwlThread = npc.id === "professor-owl";

  // Premium-gated, problem-aware helper. When a quiz/activity publishes the
  // problem the learner is looking at, premium users get a "help me with this"
  // guided explanation right where they are. Free users don't get the on-tool
  // helper (it's a paid perk on the Learning Forest & every tool).
  const isPremium = usePremiumAccess(user).premium;
  const [activeProblem, setActiveProblemState] = useState(null);
  const [learn, setLearn] = useState(null);
  const [allowReveal, setAllowReveal] = useState(false);
  // Progress-Aware Owl: the active learner + their recommended next lesson,
  // so Owl greets by name and offers a one-tap "Continue" chip.
  const [owlCtx, setOwlCtx] = useState(null);
  const academyChildId = (typeof window !== "undefined" && localStorage.getItem("academy_active_child_id")) || user?.id || user?.email || "";
  useEffect(() => onActiveProblem(setActiveProblemState), []);
  useEffect(() => onLearnContext(setLearn), []);

  const problemHelp = isPremium && !!activeProblem;
  const problemText = activeProblem
    ? [activeProblem.question, ...((activeProblem.options || []).map((o, i) => `${String.fromCharCode(65 + i)}. ${o}`))].filter(Boolean).join("\n")
    : "";

  // Auto Offer Help: after a COUPLE of misses on the current lesson, Owl
  // gently pops in and offers to walk through the problem (premium). We
  // throttle so it never nags on every single wrong answer.
  useEffect(() => {
    return onWrongAnswer(() => {
      if (!problemHelp || loading) return;
      wrongCountRef.current += 1;
      const n = wrongCountRef.current;
      const shouldOffer = n === 2 || (n > 2 && (n - 2) % 3 === 0);
      if (!shouldOffer) return;
      setOpen(true);
      if (sounds) sounds.playSound("pop");
      (async () => {
        setMessages((prev) => [...prev, { role: "npc", text: "I noticed a couple of tricky ones! 🦉 Want to walk through this together? Here's a gentle hint..." }]);
        try {
          const res = await axios.post(`${API}/npc/chat`, {
            user_id: user?.id || user?.email || "", npc_id: npc.id,
            message: "I just missed this a couple of times. Give me ONE gentle hint to rethink it, do not tell me the answer.",
            context: location.pathname, audience: "child", problem_context: problemText, allow_answer_reveal: allowReveal, hint_level: 1,
          });
          setMessages((prev) => [...prev, { role: "npc", text: res.data.response }]);
        } catch { /* noop */ }
      })();
    });
  }, [problemHelp, problemText, loading, allowReveal]);
  useEffect(() => {
    const uid = user?.id || user?.email;
    if (!uid) return;
    axios.get(`${API}/npc/tutor-settings/${uid}`).then((r) => setAllowReveal(!!r.data.allow_answer_reveal)).catch(() => {});
  }, [user]);

  // Fetch the progress-aware greeting context whenever we're inside the
  // Academy with an active learner. Re-runs on route change so the "Continue"
  // chip always points at the learner's true current next lesson.
  useEffect(() => {
    if (!alwaysOwlRoute || !isOwlThread || !academyChildId) { setOwlCtx(null); return; }
    let cancelled = false;
    axios.get(`${API}/npc/owl-greeting/${academyChildId}`)
      .then((r) => { if (!cancelled) setOwlCtx(r.data); })
      .catch(() => { if (!cancelled) setOwlCtx(null); });
    return () => { cancelled = true; };
  }, [alwaysOwlRoute, isOwlThread, academyChildId, location.pathname]);

  // Persist a single message into the shared Tutor thread. Failures are
  // silent, the floating-chat UX shouldn't block on a save error.
  const persistToTutorThread = async (role, text) => {
    if (!isOwlThread) return;
    const userId = user?.id || user?.email;
    if (!userId || !text) return;
    try {
      await axios.post(`${API}/tutor/messages`, { user_id: userId, role, text });
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    setMessages([]);
    setHasGreeted(false);
    setOpen(false);
    wrongCountRef.current = 0;
    readGenRef.current += 1;
    try { stopLawrence(); } catch { /* noop */ }
    setSpeakingIdx(null);
    setReadSent(-1);
    setReadWord(-1);
  }, [location.pathname]);

  // Read-along state clears when the panel/route changes; stop audio on unmount.
  useEffect(() => () => { try { stopLawrence(); } catch { /* noop */ } }, []);

  // Read one of Owl's answers aloud in Mr. Lawrence's voice (item 2).
  // Owl Reads Along: read a message aloud sentence-by-sentence, highlighting
  // each sentence in the bubble as it's spoken (Mr. Lawrence voice).
  const splitSpeak = (text) =>
    String(text || "").replace(/[#*_>`]/g, "").replace(/\s*[-]\s*/g, ", ").split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);

  const speakMessage = async (idx, text) => {
    if (speakingIdx === idx) { readGenRef.current += 1; try { stopLawrence(); } catch { /* noop */ } setSpeakingIdx(null); setReadSent(-1); setReadWord(-1); return; }
    readGenRef.current += 1;
    const gen = readGenRef.current;
    try { stopLawrence(); } catch { /* noop */ }
    const parts = splitSpeak(text);
    if (!parts.length) return;
    setSpeakingIdx(idx);
    for (let i = 0; i < parts.length; i++) {
      if (gen !== readGenRef.current) return;
      setReadSent(i);
      try { await playLawrence(parts[i], { rate: 0.96 }); } catch { break; }
    }
    if (gen === readGenRef.current) { setSpeakingIdx(null); setReadSent(-1); setReadWord(-1); }
  };

  // Auto-read (item, June 2026): when a NEW Owl reply arrives while the panel
  // is open, read it aloud for early readers. A mute switch in the header
  // turns it off. Hydrated history is skipped via lastReadRef.
  useEffect(() => {
    if (!autoRead || !isOwlThread || !open) return;
    const idx = messages.length - 1;
    if (idx < 0 || idx === lastReadRef.current) return;
    const m = messages[idx];
    if (m && m.role === "npc" && String(m.text || "").length > 12) {
      lastReadRef.current = idx;
      speakMessage(idx, m.text);
    }
  }, [messages, autoRead, isOwlThread, open]);

  const toggleAutoRead = () => setAutoRead((v) => {
    const nv = !v;
    try { localStorage.setItem("academy_owl_autoread", nv ? "1" : "0"); } catch { /* noop */ }
    if (!nv) { try { stopLawrence(); } catch { /* noop */ } setSpeakingIdx(null); }
    return nv;
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const greet = async (ctxArg) => {
    if (hasGreeted) return;
    setHasGreeted(true);
    // Progress-aware Owl: inside the Academy, greet by name and point at the
    // exact next lesson (deterministic + instant, no LLM cold-start). Not
    // persisted to the shared thread since it's a dynamic contextual greeting.
    const ctx = ctxArg || owlCtx;
    if (alwaysOwlRoute && isOwlThread && ctx?.greeting) {
      setMessages([{ role: "npc", text: ctx.greeting }]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/npc/chat`, {
        user_id: user?.id || user?.email || "",
        npc_id: npc.id,
        message: "Hi! What is this place?",
        context: location.pathname,
      });
      setMessages([{ role: "npc", text: res.data.response }]);
      // Mirror the greeting into the persistent thread so the user sees
      // it in /learn/tutor too. We tag the npc reply with role "ai" to
      // match the TutorPage convention.
      await persistToTutorThread("ai", res.data.response);
    } catch {
      setMessages([{ role: "npc", text: `Hi there, explorer! I'm ${npc.name}. Ask me anything!` }]);
    }
    setLoading(false);
  };

  // When opening the floating widget on a Learn page, hydrate the chat
  // with the last few messages from the shared Tutor thread so the user
  // doesn't see an empty box even though they have an ongoing
  // conversation. We only show the tail (last 6) to keep the popup
  // compact, the full history lives at /learn/tutor.
  const hydrateFromTutorThread = async () => {
    if (!isOwlThread) return false;
    const userId = user?.id || user?.email;
    if (!userId) return false;
    try {
      const r = await axios.get(`${API}/tutor/messages/${userId}`);
      const all = r.data?.messages || [];
      if (!all.length) return false;
      const tail = all.slice(-6).map(m => ({
        role: m.role === "user" ? "user" : "npc",
        text: m.text,
      }));
      setMessages(tail);
      lastReadRef.current = tail.length - 1;  // don't auto-read hydrated history
      setHasGreeted(true);
      return true;
    } catch {
      return false;
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    // Opening the chat also brings the Isaiah companion mascot back if the
    // user had minimized it with the X.
    try { window.dispatchEvent(new CustomEvent("isaiah:restore-companion")); } catch {}
    if (sounds) sounds.playSound("pop");
    // Make sure the progress-aware greeting context is ready before we greet.
    let ctx = owlCtx;
    if (alwaysOwlRoute && isOwlThread && !ctx && academyChildId) {
      try {
        const r = await axios.get(`${API}/npc/owl-greeting/${academyChildId}`);
        ctx = r.data;
        setOwlCtx(r.data);
      } catch { /* fall back to generic greeting */ }
    }
    // For Owl threads, try to load existing history first; only greet
    // if there's nothing to show yet.
    if (isOwlThread) {
      const hydrated = await hydrateFromTutorThread();
      if (hydrated) return;
    }
    if (!hasGreeted) greet(ctx);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    if (sounds) sounds.playSound("click");
    // Persist the user's question into the shared thread first so it's
    // never lost, even if the AI call fails, the question stays in
    // the message history and the user can re-ask later.
    await persistToTutorThread("user", userMsg);
    try {
      // Audience + current-date passthrough (matches TutorPage). Lets
      // the same Owl personality adapt its tone for parents vs kids
      // and answer date/time questions correctly. (May 2026)
      const audience = (user?.role === "parent" || user?.role === "admin" || user?.role === "super_admin") ? "parent" : "child";
      const currentDateStr = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
      const res = await axios.post(`${API}/npc/chat`, {
        user_id: user?.id || user?.email || "",
        npc_id: npc.id,
        message: userMsg,
        context: location.pathname,
        audience,
        current_date_str: currentDateStr,
        problem_context: problemHelp ? problemText : "",
        allow_answer_reveal: allowReveal,
      });
      setMessages(prev => [...prev, { role: "npc", text: res.data.response }]);
      await persistToTutorThread("ai", res.data.response);
    } catch {
      setMessages(prev => [...prev, { role: "npc", text: "Hmm, my brain got a little fuzzy. Try asking again!" }]);
    }
    setLoading(false);
  };

  // Guided help for the exact on-screen problem (premium only).
  const askOwlQuick = async (displayMsg, ctxText, { hint = false } = {}) => {
    if (loading) return;
    setOpen(true);
    if (sounds) sounds.playSound("pop");
    setMessages((prev) => [...prev, { role: "user", text: displayMsg }]);
    setLoading(true);
    // Embed the exact on-screen content INTO the message so Owl always
    // addresses it directly (never "tell me what the problem says"). The
    // visible bubble stays clean, only the sent + persisted text carries it.
    const sendMsg = ctxText
      ? `${displayMsg}\n\nHere is exactly what is on my screen right now, use it directly, don't ask me to retype it:\n${ctxText}`
      : displayMsg;
    await persistToTutorThread("user", sendMsg);
    try {
      const audience = (user?.role === "parent" || user?.role === "admin" || user?.role === "super_admin") ? "parent" : "child";
      const res = await axios.post(`${API}/npc/chat`, {
        user_id: user?.id || user?.email || "", npc_id: npc.id,
        message: sendMsg, context: location.pathname, audience,
        current_date_str: new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
        problem_context: ctxText || "", allow_answer_reveal: allowReveal,
        hint_level: hint ? 1 : 0,
      });
      setMessages((prev) => [...prev, { role: "npc", text: res.data.response }]);
      await persistToTutorThread("ai", res.data.response);
    } catch {
      setMessages((prev) => [...prev, { role: "npc", text: "Let's figure this out together! Which part feels tricky?" }]);
    }
    setLoading(false);
  };

  // Build the context-aware quick suggestions from whatever is on screen.
  const lessonCtx = learn
    ? [learn.lessonTitle ? `Lesson: ${learn.lessonTitle}` : "", (learn.lessonText || "").slice(0, 1500)].filter(Boolean).join("\n")
    : "";
  const quickChips = [];
  // Progress-Aware Owl: one-tap jump to the learner's recommended next lesson.
  if (owlCtx?.has_action && owlCtx.action?.lesson_id
      && location.pathname !== `/academy/lesson/${owlCtx.action.lesson_id}`) {
    const verb = owlCtx.action.kind === "start" ? "Start" : "Continue";
    quickChips.push({
      id: "continue",
      label: `▶️ ${verb}: ${owlCtx.action.title}`,
      run: () => { setOpen(false); navigate(`/academy/lesson/${owlCtx.action.lesson_id}`); },
    });
  }
  if (problemHelp) {
    quickChips.push({ id: "solve", label: "🧩 Solve the problem on my screen", run: () => askOwlQuick("Can you walk me through the problem on my screen, step by step?", problemText) });
    quickChips.push({ id: "hint", label: "💡 Give me a hint", run: () => askOwlQuick("I'm stuck on this, give me ONE gentle hint, please don't tell me the answer.", problemText, { hint: true }) });
  }
  if (learn && (learn.lessonTitle || learn.lessonText)) {
    quickChips.push({ id: "explain", label: "📖 Explain this lesson more", run: () => askOwlQuick("Can you explain this lesson in a simpler way, with an easy example?", lessonCtx) });
  }
  if (learn && learn.subject) {
    quickChips.push({ id: "subject", label: `🔎 More about ${learn.subject}`, run: () => askOwlQuick(`Tell me more about ${learn.subject} in a fun, simple way I can understand.`, lessonCtx) });
  }
  // Explain what I highlighted (item 3): watch text selections inside the
  // Academy and offer a floating "Explain this" button near the selection.
  useEffect(() => {
    if (!inAcademyRoute) { setSelection(null); return; }
    const onSel = () => {
      try {
        const sel = window.getSelection();
        const text = sel ? String(sel.toString()).trim() : "";
        if (!text || text.length < 4 || text.length > 400 || sel.rangeCount === 0) { setSelection(null); return; }
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        if (!rect || (!rect.width && !rect.height)) { setSelection(null); return; }
        setSelection({
          text,
          x: Math.min(Math.max(rect.left + rect.width / 2, 70), window.innerWidth - 70),
          y: Math.max(rect.top - 6, 52),
        });
      } catch { setSelection(null); }
    };
    const onSelChange = () => { const s = window.getSelection(); if (!s || !String(s).trim()) setSelection(null); };
    document.addEventListener("mouseup", onSel);
    document.addEventListener("touchend", onSel);
    document.addEventListener("selectionchange", onSelChange);
    return () => {
      document.removeEventListener("mouseup", onSel);
      document.removeEventListener("touchend", onSel);
      document.removeEventListener("selectionchange", onSelChange);
    };
  }, [inAcademyRoute]);

  const explainSelection = () => {
    const t = selection?.text;
    if (!t) return;
    setSelection(null);
    try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ }
    askOwlQuick("Can you explain this part to me in a simple way?", `The part I highlighted: "${t}"`);
  };

  // Snap a Question (item 4): attach a photo of a problem and get step-by-step
  // help, same pipeline as the full tutor's Upload & Learn.
  const onSnapFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || uploading) return;
    if (f.size > 12 * 1024 * 1024) {
      setOpen(true);
      setMessages((prev) => [...prev, { role: "npc", text: "That photo is a bit big, try one under 12 MB. 🦉" }]);
      return;
    }
    setOpen(true);
    setMessages((prev) => [...prev, { role: "user", text: `📎 ${f.name}` }]);
    setUploading(true);
    setLoading(true);
    if (sounds) sounds.playSound("click");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("message", input.trim() || "Can you help me with this? Walk me through it step by step, please.");
      fd.append("user_id", user?.id || user?.email || "");
      fd.append("npc_id", "professor-owl");
      fd.append("personality_mode", "owl");
      const audience = (user?.role === "parent" || user?.role === "admin" || user?.role === "super_admin") ? "parent" : "child";
      fd.append("audience", audience);
      fd.append("current_date_str", new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }));
      fd.append("allow_answer_reveal", allowReveal ? "true" : "false");
      const r = await axios.post(`${API}/tutor/chat-with-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setMessages((prev) => [...prev, { role: "npc", text: r.data.response || "I had trouble reading that, try a clearer photo?" }]);
      setInput("");
    } catch {
      setMessages((prev) => [...prev, { role: "npc", text: "Hmm, I couldn't read that one. Try a clearer photo! 🦉" }]);
    }
    setUploading(false);
    setLoading(false);
  };

  const recordingTimeoutRef = useRef(null);
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/ogg") ? "audio/ogg"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "mp4" : "webm";
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearTimeout(recordingTimeoutRef.current);
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        if (blob.size > 0) {
          try {
            const formData = new FormData();
            formData.append("audio", blob, `voice.${ext}`);
            const res = await axios.post(`${API}/art-creator/voice-to-text`, formData);
            const text = res.data.text || res.data.transcribed || "";
            if (text.trim()) { setInput(text); }
          } catch { /* transcription optional */ }
        }
        setRecording(false);
      };
      mr.start(250);
      mediaRecorderRef.current = mr;
      setRecording(true);
      recordingTimeoutRef.current = setTimeout(() => stopRecording(), 40000);
    } catch { setRecording(false); }
  };

  const stopRecording = () => {
    clearTimeout(recordingTimeoutRef.current);
    setRecording(false);
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { console.error("Stop recording error:", e); }
  };

  if (fabsCollapsed) return null;
  // Professor Owl has its own full-screen tutor chat; the floating site-wide
  // helper is redundant there and overlaps the input bar, so hide it on that page.
  if (location.pathname.startsWith("/learn/tutor")) return null;
  return (
    <div className="npc-guide-container" data-testid="npc-guide" style={owlDrag.style} {...owlDrag.bind}>
      {/* On-tool helper is premium-only when a live problem is present; on hub
          pages the widget shows for everyone. Inside the Academy it is ALWAYS
          available (Professor Owl, no leaving the screen). */}
      {(alwaysOwlRoute ? false : ((!isPremium && onLearnSubject) ? true : (activeProblem ? !isPremium : isInsideGame))) ? null : (
      <>
      {open && (
        <div className={`npc-chat-bubble${expanded ? " npc-chat-bubble--full" : ""}`} data-testid="npc-chat-bubble"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{npc.emoji}</span>
              <span className="text-white text-xs font-fredoka font-bold">{npc.name}</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Auto-read mute switch (early readers) */}
              {isOwlThread && (
                <button
                  onClick={toggleAutoRead}
                  className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-1 rounded-md transition-colors ${autoRead ? "text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`}
                  title={autoRead ? "Auto read-aloud is ON, tap to mute" : "Auto read-aloud is OFF, tap to turn on"}
                  data-testid="npc-autoread-toggle"
                >
                  {autoRead ? <Volume2 size={12} /> : <VolumeX size={12} />}
                </button>
              )}
              {/* Open the full Tutor thread, carry a return path so the
                  learner can jump straight back to the exact activity. */}
              {isOwlThread && (
                <button
                  onClick={() => {
                    setOpen(false);
                    const ret = inAcademyRoute ? `?return=${encodeURIComponent(location.pathname + location.search)}` : "";
                    navigate(`/learn/tutor${ret}`);
                  }}
                  className="flex items-center gap-1 text-white/50 hover:text-amber-300 text-[10px] font-bold uppercase tracking-wider px-1.5 py-1 rounded-md hover:bg-white/5 transition-colors"
                  title="Open full message thread"
                  data-testid="npc-open-full-thread"
                >
                  <Maximize2 size={11} />
                  Full thread
                </button>
              )}
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-white/40 hover:text-white/80 transition-colors p-1"
                data-testid="npc-expand"
                title={expanded ? "Shrink chat" : "Expand chat to fullscreen"}
                aria-label={expanded ? "Shrink chat" : "Expand chat to fullscreen"}
              >
                {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white/80 transition-colors p-1" data-testid="npc-close">
                <X size={14} />
              </button>
            </div>
          </div>
          {quickChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-2 mb-1" data-testid="npc-quick-chips">
              {quickChips.map((c) => (
                <button
                  key={c.id}
                  onClick={c.run}
                  disabled={loading}
                  className="whitespace-nowrap text-[11px] font-bold text-emerald-100 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2.5 py-1.5 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                  data-testid={`npc-chip-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1 mb-2" style={{ scrollbarWidth: "thin" }}>
            {messages.map((m, i) => (
              m.role === "npc" ? (
                <div key={i} className="flex items-start gap-1.5" data-testid={`npc-msg-${i}`}>
                  <span className="text-sm mt-0.5 flex-shrink-0">{npc.emoji}</span>
                  <div className="flex-1 min-w-0 rounded-2xl rounded-tl-sm bg-white/[0.06] border border-white/10 px-3 py-2">
                    <div className="npc-msg-text text-[12.5px] leading-relaxed text-white/90 break-words">
                      {speakingIdx === i ? (
                        <span>
                          {splitSpeak(m.text).map((s, si) => {
                            if (si !== readSent) return <span key={si} className="text-white/60">{s}{" "}</span>;
                            const words = s.split(/\s+/).filter(Boolean);
                            return (
                              <span key={si} className="rounded bg-amber-300/15">
                                {words.map((w, wi) => (
                                  <span key={wi} className={wi === readWord ? "bg-amber-400/70 text-slate-900 rounded px-0.5" : "text-white"}>{w}{wi < words.length - 1 ? " " : ""}</span>
                                ))}
                                {" "}
                              </span>
                            );
                          })}
                        </span>
                      ) : (
                        <RichText text={m.text} />
                      )}
                    </div>
                    {isOwlThread && String(m.text || "").length > 12 && (
                      <button
                        onClick={() => speakMessage(i, m.text)}
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${speakingIdx === i ? "bg-emerald-500/30 text-emerald-200" : "bg-white/5 text-white/40 hover:text-emerald-200 hover:bg-emerald-500/15"}`}
                        title={speakingIdx === i ? "Stop reading" : "Read this aloud"}
                        data-testid={`npc-speak-${i}`}
                      >
                        {speakingIdx === i ? <Square size={10} /> : <Volume2 size={10} />}
                        {speakingIdx === i ? "Stop" : "Read aloud"}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex justify-end" data-testid={`npc-msg-${i}`}>
                  <div className="npc-msg-text max-w-[80%] rounded-2xl rounded-tr-sm bg-blue-500/20 border border-blue-400/20 px-3 py-2 text-[12.5px] leading-relaxed text-blue-100 break-words">
                    <RichText text={m.text} />
                  </div>
                </div>
              )
            ))}
            {loading && (
              <div className="flex items-center gap-1">
                <span className="text-white/40 text-xs">{npc.emoji}</span>
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="flex gap-1.5">
            <button onClick={recording ? stopRecording : startRecording} className={`p-2 rounded-xl transition-all flex-shrink-0 ${recording ? "bg-red-500/20 text-red-400 animate-pulse" : "bg-white/5 text-white/30 hover:text-white/60 hover:bg-white/10"}`} title={recording ? "Stop recording" : "Voice input"} data-testid="npc-mic">
              {recording ? <Square size={14} /> : <Mic size={14} />}
            </button>
            {isOwlThread && (
              <>
                <input ref={fileInputRef} type="file" accept="image/*,.pdf" capture="environment" onChange={onSnapFile} className="hidden" data-testid="npc-snap-input" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading || loading} className="p-2 rounded-xl bg-white/5 text-white/30 hover:text-cyan-200 hover:bg-cyan-500/15 transition-all flex-shrink-0 disabled:opacity-40" title="Snap a photo of a question" data-testid="npc-snap">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                </button>
              </>
            )}
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              placeholder={recording ? "Listening..." : `Ask ${npc.name}...`}
              className="npc-chat-input flex-1"
              maxLength={150}
              data-testid="npc-input"
            />
            <button onClick={sendMessage} disabled={loading || !input.trim()} className="p-2 rounded-xl bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-40" data-testid="npc-send">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
      {/* Explain what I highlighted (item 3), floating near the selection */}
      {inAcademyRoute && selection && !open && (
        <button
          onClick={explainSelection}
          className="fixed z-[95] -translate-x-1/2 -translate-y-full inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[11px] font-fredoka font-bold shadow-lg border border-emerald-300/50"
          style={{ left: selection.x, top: selection.y }}
          data-testid="npc-explain-selection"
        >
          🦉 Explain this
        </button>
      )}
      {/* Hidden: show only a small peek tab in the corner to bring it back. */}
      {npcHidden && !open ? (
        <button
          onClick={showNpc}
          data-testid="npc-restore-tab"
          aria-label={`Show ${npc.name}`}
          className="fixed bottom-[256px] right-3 z-[60] flex items-center justify-center isaiah-peek-wiggle"
          style={{ width: 26, height: 44, borderTopLeftRadius: 9999, borderBottomLeftRadius: 9999,
            border: `1px solid ${npc.color}55`, borderRight: "none", background: `${npc.color}CC`, boxShadow: "0 4px 14px rgba(0,0,0,0.35)" }}
        >
          <span style={{ fontSize: 16 }}>{npc.emoji}</span>
        </button>
      ) : (
      <div className="relative">
        {!open && (
          <button onClick={hideNpc} data-testid="npc-hide-btn" aria-label={`Hide ${npc.name}`}
            className="absolute -top-1 -right-1 z-10 w-4 h-4 rounded-full bg-slate-900 border border-white/60 flex items-center justify-center shadow hover:bg-black hover:scale-110 transition-all">
            <X size={10} className="text-white/90" />
          </button>
        )}
        <button onClick={() => { if (owlDrag.shouldSwallowClick()) return; if (open) setOpen(false); else handleOpen(); }} className="npc-avatar-btn" style={{ borderColor: `${npc.color}40`, boxShadow: `0 0 20px ${npc.color}20` }} data-testid="npc-toggle">
          {open ? <X size={22} className="text-white/60" /> : <span>{npc.emoji}</span>}
        </button>
      </div>
      )}
      </>
      )}
    </div>
  );
};

export default NPCGuide;
